import React, {useEffect, useState, useRef} from "react";
import IconButton from "@material-ui/core/IconButton";
import Slider from "@material-ui/core/Slider";
import {
  PlayArrow,
  Pause,
  Fullscreen,
  FullscreenExit,
  VolumeUp,
  VolumeOff,
} from "@material-ui/icons";
import pauseIcon from "../../../Assets/Images/pause.svg";

// Helper to format seconds into mm:ss
const formatTime = (seconds) => {
  const m = Math.floor(seconds / 60).toString();
  const s = Math.floor(seconds % 60)
    .toString()
    .padStart(2, "0");
  return `${m}:${s}`;
};

const SecureVideoPlayer = ({ url, title = "", onComplete }) => {
  const videoRef = useRef(null);
  const containerRef = useRef(null);

  const [isPlaying, setIsPlaying] = useState(false);
  const [volume, setVolume] = useState(1);
  const [isMuted, setIsMuted] = useState(false);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [maxWatchTime, setMaxWatchTime] = useState(0);
  const [hovered, setHovered] = useState(false);
  const [loading, setLoading] = useState(false);

  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);

  useEffect(() => {
    const video = videoRef.current;
    if (!video) return;

    video.volume = volume;

    const handleLoadedMetadata = () => setDuration(video.duration || 0);
    const handleTimeUpdate = () => {
      setCurrentTime(video.currentTime);
      if (video.currentTime > maxWatchTime) {
        setMaxWatchTime(video.currentTime);
      }
    };

    const handleSeeking = () => {
      const current = video.currentTime;
      // Throttle revert to allow seeking to buffer
      setTimeout(() => {
        if (video.currentTime > maxWatchTime) {
          video.currentTime = maxWatchTime;
        }
      }, 100);
    };

    const handleEnded = () => {
      setIsPlaying(false);
      if (onComplete) onComplete();
    };

    let loadingTimeout;
    const handleWaiting = () => {
      clearTimeout(loadingTimeout);
      loadingTimeout = setTimeout(() => {
        if (
          !videoRef.current?.paused &&
          !videoRef.current?.ended &&
          videoRef.current.readyState < 3
        ) {
          setLoading(true);
        }
      }, 500); // wait 200ms before showing spinner
    };

    const handleCanPlay = () => setLoading(false);
    const handlePlaying = () => {
      clearTimeout(loadingTimeout);
      setLoading(false);
    };

    const handleKeyDown = (e) => {
      if (e.key === "Escape" && isFullscreen) {
        document.exitFullscreen().then(() => setIsFullscreen(false));
      }
    };

    video.addEventListener("loadedmetadata", handleLoadedMetadata);
    video.addEventListener("timeupdate", handleTimeUpdate);
    video.addEventListener("seeking", handleSeeking);
    video.addEventListener("ended", handleEnded);
    video.addEventListener("waiting", handleWaiting);
    video.addEventListener("canplay", handleCanPlay);
    video.addEventListener("canplaythrough", handleCanPlay);
    video.addEventListener("playing", handlePlaying);
    document.addEventListener("keydown", handleKeyDown);

    return () => {
      video.removeEventListener("loadedmetadata", handleLoadedMetadata);
      video.removeEventListener("timeupdate", handleTimeUpdate);
      video.removeEventListener("seeking", handleSeeking);
      video.removeEventListener("ended", handleEnded);
      video.removeEventListener("waiting", handleWaiting);
      video.removeEventListener("canplay", handleCanPlay);
      video.removeEventListener("canplaythrough", handleCanPlay);
      video.removeEventListener("playing", handlePlaying);
      document.removeEventListener("keydown", handleKeyDown);
    };
  }, [maxWatchTime, onComplete, volume, isFullscreen]);

  const togglePlayPause = () => {
    const video = videoRef.current;
    if (!video) return;

    if (video.ended || video.currentTime === video.duration) {
      video.currentTime = 0;
      // video.load(); // force reload
      video.play();
      setIsPlaying(true);
      setLoading(false); // don't show loader on manual play
      return;
    }

    if (video.paused) {
      video.play();
      setIsPlaying(true);
      setLoading(false);
    } else {
      video.pause();
      setIsPlaying(false);
    }
  };

  const toggleMute = () => {
    const video = videoRef.current;
    if (!video) return;

    video.muted = !video.muted;
    setIsMuted(video.muted);
  };

  const handleVolumeChange = (_, newValue) => {
    const val = newValue ?? 0;
    setVolume(val);
    if (videoRef.current) {
      videoRef.current.volume = val;
      videoRef.current.muted = val === 0;
      setIsMuted(val === 0);
    }
  };

  const toggleFullscreen = () => {
    const container = containerRef.current;
    if (!container) return;

    if (!document.fullscreenElement) {
      container.requestFullscreen().then(() => setIsFullscreen(true));
    } else {
      document.exitFullscreen().then(() => setIsFullscreen(false));
    }
  };

  return (
    <div
      ref={containerRef}
      style={{
        position: "relative",
        // maxWidth: 640,
        margin: "0 auto",
        background: "#000",
        // borderRadius: 8,
        overflow: "hidden",
        aspectRatio: "16/9",
        cursor: "pointer",
        minHeight: "360px",
      }}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      onClick={togglePlayPause}
    >
      {/* Title */}
      {title && (
        <div
          style={{
            position: "absolute",
            top: 0,
            left: 0,
            right: 0,
            backgroundColor: "rgba(0, 0, 0, 0.6)",
            color: "#fff",
            padding: "10px 16px",
            fontSize: 16,
            fontWeight: "bold",
            opacity: hovered || isFullscreen ? 1 : 0,
            transition: "opacity 0.3s ease-in-out",
            zIndex: 5,
          }}
        >
          {title}
        </div>
      )}

      {/* Play overlay (only when paused and not loading) */}
      {!isPlaying && !loading && (
        <div
          onClick={(e) => {
            e.stopPropagation(); // prevent double toggle
            togglePlayPause();
          }}
          style={{
            position: "absolute",
            top: "50%",
            left: "50%",
            transform: "translate(-50%, -50%)",
            color: "#fff",
            fontSize: 40,
            zIndex: 10,
            // background: "rgba(0,0,0,0.5)",
            borderRadius: "50%",
            // padding: "10px 20px",
          }}
        >
          <img src={pauseIcon} />
        </div>
      )}

      {/* Loading Spinner */}
      {loading && (
        <div
          style={{
            position: "absolute",
            top: "50%",
            left: "50%",
            zIndex: 6,
            transform: "translate(-50%, -50%)",
          }}
        >
          <div className="spinner" />
        </div>
      )}

      {/* Video */}
      <video
        ref={videoRef}
        src={url}
        preload="auto"
        style={{
          width: "100%",
          height: "100%",
          display: "block",
          minHeight: "200px",
        }}
        playsInline
        disablePictureInPicture
      />

      {/* Controls */}
      <div
        style={{
          position: "absolute",
          bottom: 0,
          left: 0,
          right: 0,
          background: "rgba(0,0,0,0.5)",
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          padding: "6px 10px",
          opacity: hovered || isFullscreen ? 1 : 0,
          transition: "opacity 0.3s ease-in-out",
          zIndex: 6,
        }}
        onClick={(e) => e.stopPropagation()}
      >
        <IconButton onClick={togglePlayPause} sx={{ color: "white" }}>
          {isPlaying ? <Pause style={{ color: "white" }} /> : <PlayArrow style={{ color: "white" }}/>}
        </IconButton>

        <div style={{ display: "flex", alignItems: "center" }}>
          <IconButton onClick={toggleMute} sx={{ color: "white" }}>
            {isMuted || volume === 0 ? <VolumeOff style={{ color: "white" }}/> : <VolumeUp style={{ color: "white" }}/>}
          </IconButton>
          <Slider
            value={volume}
            min={0}
            max={1}
            step={0.01}
            onChange={handleVolumeChange}
            style={{ width: 80, color: "white" }}
          />
        </div>

        <div style={{ color: "#fff", fontSize: 14, minWidth: 60 }}>
          {formatTime(currentTime)} / {formatTime(duration)}
        </div>

        <IconButton onClick={toggleFullscreen} style={{ color: "white" }}>
          {isFullscreen ? <FullscreenExit style={{ color: "white" }}/> : <Fullscreen style={{ color: "white" }}/>}
        </IconButton>
      </div>

      {/* Spinner Style */}
      <style>{`
        .spinner {
          width: 40px;
          height: 40px;
          border: 4px solid rgba(255,255,255,0.3);
          border-top-color: #fff;
          border-radius: 50%;
          animation: spin 1s linear infinite;
        }

        @keyframes spin {
          to { transform: rotate(360deg); }
        }
      `}</style>
    </div>
  );
};

export default SecureVideoPlayer;
